import type {
  NotificationType,
  ServerNotification,
} from '@openeducation/oe-it-shared-types';
import { convertToClientTimezone } from '@openeducation/oe-it-utils';
import { differenceInMinutes } from 'date-fns';

import { DateService } from './date.service';
import NotificationDao from '../dao/notification.dao';
import type { Environment } from '../interfaces/environment';

const SHOW_NOTIFICATION_LIMIT = 5;

export abstract class NotificationService {
  private static transformNotification<T extends ServerNotification>(
    notification: ServerNotification
  ): T {
    const now = DateService.getNow();
    const expirationDate = convertToClientTimezone(
      new Date(notification.expirationDate)
    );
    const expirationMs = expirationDate.getTime() - now.getTime();

    return {
      ...notification,
      timeOfClass: convertToClientTimezone(new Date(notification.timeOfClass)),
      notificationDate: convertToClientTimezone(
        new Date(notification.notificationDate)
      ),
      expirationDate,
      expirationMs,
    } as unknown as T;
  }

  private static filterActiveNotifications(
    notifications: ServerNotification[] | undefined
  ) {
    if (!notifications) {
      return [];
    }

    const now = DateService.getNow();
    return notifications?.filter((notification) => {
      const expirationDate = convertToClientTimezone(
        new Date(notification.expirationDate)
      );
      const timeOfClass = convertToClientTimezone(
        new Date(notification.timeOfClass)
      );
      const minutesToClass = differenceInMinutes(timeOfClass, now);

      return (
        expirationDate > now &&
        (!notification.timeOfClass || minutesToClass < SHOW_NOTIFICATION_LIMIT)
      );
    });
  }

  private static hasOnlyLiveClassNotifications(
    notifications: ServerNotification[]
  ) {
    return (
      notifications.every(
        (notification) => notification.type === 'LiveClassNotification'
      ) && notifications.length > 1
    );
  }

  private static filterLiveClassNotifications(
    notifications: ServerNotification[]
  ) {
    if (!this.hasOnlyLiveClassNotifications(notifications)) {
      return notifications;
    }

    return notifications
      .sort(
        (a, b) =>
          new Date(a.notificationDate).getTime() -
          new Date(b.notificationDate).getTime()
      )
      .slice(0, 1);
  }

  static async getNotifications<T extends ServerNotification>(
    env: Environment,
    teacherId?: number,
    notificationType?: NotificationType
  ): Promise<T[]> {
    if (!teacherId) {
      throw new Error('Missing teacherId');
    }

    const notifications = await NotificationDao.getNotifications(
      env,
      teacherId,
      notificationType
    );

    const activeNotifications = this.filterActiveNotifications(notifications);
    const filteredNotifications =
      this.filterLiveClassNotifications(activeNotifications);

    return filteredNotifications.map<T>(this.transformNotification);
  }

  static async confirm(
    env: Environment,
    notificationId: string,
    personId?: number
  ) {
    if (!notificationId) {
      throw new Error('Missing notificationId');
    }
    if (!personId) {
      throw new Error('Missing personId');
    }
    return await NotificationDao.confirm(env, notificationId, personId);
  }

  static async acknowledge(
    env: Environment,
    notificationId: string,
    personId?: number
  ) {
    if (!notificationId) {
      throw new Error('Missing notificationId');
    }
    if (!personId) {
      throw new Error('Missing personId');
    }
    return await NotificationDao.acknowledge(env, notificationId, personId);
  }
}
